<script>
import AutomationActionTeamMessageInput from './AutomationActionTeamMessageInput.vue';
import AutomationActionFileInput from './AutomationFileInput.vue';
import SlackNotificationInput from './SlackNotificationInput.vue';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import SingleSelect from 'dashboard/components-next/filter/inputs/SingleSelect.vue';
import MultiSelect from 'dashboard/components-next/filter/inputs/MultiSelect.vue';
import CheckBoxSelect from 'dashboard/components-next/filter/inputs/CheckBoxSelect.vue';
import NextInput from 'dashboard/components-next/input/Input.vue';

export default {
  components: {
    SlackNotificationInput,
    AutomationActionTeamMessageInput,
    AutomationActionFileInput,
    WootMessageEditor,
    NextButton,
    SingleSelect,
    MultiSelect,
    CheckBoxSelect,
    NextInput,
  },
  props: {
    modelValue: {
      type: Object,
      default: () => null,
    },
    actionTypes: {
      type: Array,
      default: () => [],
    },
    dropdownValues: {
      type: Array,
      default: () => [],
    },
    errorMessage: {
      type: String,
      default: '',
    },
    showActionInput: {
      type: Boolean,
      default: true,
    },
    initialFileName: {
      type: String,
      default: '',
    },
    isMacro: {
      type: Boolean,
      default: false,
    },
    customAttributes: {
      type: Array,
      default: () => [],
    },
    dropdownMaxHeight: {
      type: String,
      default: 'max-h-80',
    },
  },
  emits: ['update:modelValue', 'input', 'removeAction', 'resetAction'],
  computed: {
    action_name: {
      get() {
        if (!this.modelValue) return null;
        return this.modelValue.action_name;
      },
      set(value) {
        const payload = this.modelValue || {};
        this.$emit('update:modelValue', { ...payload, action_name: value });
        this.$emit('input', { ...payload, action_name: value });
      },
    },
    action_params: {
      get() {
        if (!this.modelValue) return null;
        return this.modelValue.action_params;
      },
      set(value) {
        const payload = this.modelValue || {};
        this.$emit('update:modelValue', { ...payload, action_params: value });
        this.$emit('input', { ...payload, action_params: value });
      },
    },
    inputType() {
      const currentAction = this.actionTypes.find(
        item => item.key === this.action_name
      );
      return currentAction ? currentAction.inputType : null;
    },
    actionNameAsSelectModel: {
      get() {
        if (!this.action_name) return null;
        const found = this.actionTypes.find(a => a.key === this.action_name);
        return found ? { id: found.key, name: found.label } : null;
      },
      set(value) {
        this.action_name = value?.id || value;
      },
    },
    actionTypesAsOptions() {
      return this.actionTypes.map(a => ({ id: a.key, name: a.label }));
    },
    isVerticalLayout() {
      return ['team_message', 'textarea', 'slack_notify'].includes(
        this.inputType
      );
    },

    castMessageVmodel: {
      get() {
        const params = this.action_params;

        if (Array.isArray(params)) {
          const first = params[0];

          if (typeof first === 'string') {
            return first;
          }
          if (first && typeof first === 'object') {
            if (typeof first.message === 'string') return first.message;
            if (typeof first.content === 'string') return first.content;
          }

          return '';
        }
        if (typeof params === 'string') {
          return params;
        }
        return '';
      },
      set(value) {
        this.action_params = value || '';
      },
    },

    customAttributeConfig: {
      get() {
        if (!this.modelValue) {
          return {
            attribute_key: '',
            attribute_model: 'conversation_attribute',
            values: [],
          };
        }

        const params = this.action_params;
        const config = (Array.isArray(params) ? params[0] : params) || {};

        const rawValues = config.values;
        let values = [];

        if (Array.isArray(rawValues)) {
          values = rawValues.map(v => String(v));
        } else if (rawValues != null && rawValues !== '') {
          values = [String(rawValues)];
        }

        return {
          attribute_key: config.attribute_key || '',
          attribute_model: config.attribute_model || 'conversation_attribute',
          values,
        };
      },
      set(newConfig) {
        const rawValues = newConfig.values;
        let values = [];

        if (Array.isArray(rawValues)) {
          values = rawValues.map(v => String(v));
        } else if (rawValues != null && rawValues !== '') {
          values = [String(rawValues)];
        }

        const normalized = {
          attribute_key: newConfig.attribute_key || '',
          attribute_model:
            newConfig.attribute_model || 'conversation_attribute',
          values,
        };

        this.action_params = [normalized];
      },
    },

    customAttributeOptions() {
      if (!Array.isArray(this.customAttributes)) {
        return [];
      }

      const action = this.action_name;

      const filtered = this.customAttributes.filter(attr => {
        const displayType = attr.attribute_display_type;
        if (
          action === 'add_to_custom_attribute_list' ||
          action === 'remove_from_custom_attribute_list'
        ) {
          return displayType === 'list';
        }

        if (action === 'update_custom_attribute') {
          return true;
        }

        return displayType === 'list';
      });

      return filtered.map(attr => {
        const model = attr.attribute_model || 'conversation_attribute';
        const groupLabel =
          model === 'contact_attribute' ? 'Contact' : 'Conversation';

        return {
          id: `${model}:${attr.attribute_key}`,
          attribute_key: attr.attribute_key,
          attribute_model: model,
          name: `[${groupLabel}] ${
            attr.attribute_display_name || attr.attribute_key
          }`,
          raw: attr,
        };
      });
    },

    customAttributeSelected: {
      get() {
        const { attribute_key, attribute_model } = this.customAttributeConfig;
        if (!attribute_key) return null;

        const id = `${attribute_model}:${attribute_key}`;
        return this.customAttributeOptions.find(opt => opt.id === id) || null;
      },
      set(option) {
        if (!option) {
          this.customAttributeConfig = {
            attribute_key: '',
            attribute_model: 'conversation_attribute',
            values: [],
          };
          return;
        }

        this.customAttributeConfig = {
          ...this.customAttributeConfig,
          attribute_key: option.attribute_key,
          attribute_model: option.attribute_model,
        };
      },
    },

    customAttributeSelectedDisplayType() {
      const selected = this.customAttributeSelected;
      if (!selected || !selected.raw) return null;
      return selected.raw.attribute_display_type || null;
    },

    customAttributeValuesOptions() {
      const selected = this.customAttributeSelected;
      if (!selected || !selected.raw) return [];

      const raw = selected.raw;
      let values = raw.attribute_values;

      if (Array.isArray(values)) {
        return values
          .map(v => String(v))
          .filter(Boolean)
          .map(v => ({ id: v, name: v }));
      }

      if (typeof values === 'string') {
        return values
          .split(',')
          .map(v => v.trim())
          .filter(Boolean)
          .map(v => ({ id: v, name: v }));
      }

      return [];
    },

    customAttributeSelectedValues: {
      get() {
        const currentValues = this.customAttributeConfig.values.map(v =>
          String(v)
        );
        return this.customAttributeValuesOptions.filter(opt =>
          currentValues.includes(String(opt.id))
        );
      },
      set(newOptions) {
        const values = Array.isArray(newOptions)
          ? newOptions.map(o => String(o.id))
          : [];
        this.customAttributeConfig = {
          ...this.customAttributeConfig,
          values,
        };
      },
    },

    customAttributeSingleValue: {
      get() {
        const vals = this.customAttributeConfig.values;
        return Array.isArray(vals) && vals.length ? vals[0] : '';
      },
      set(val) {
        const value = val != null ? String(val) : '';
        this.customAttributeConfig = {
          ...this.customAttributeConfig,
          values: value === '' ? [] : [value],
        };
      },
    },

    checkboxOptions() {
      return [
        { id: 'true', name: 'True' },
        { id: 'false', name: 'False' },
      ];
    },
    customAttributeCheckboxOption: {
      get() {
        const v = String(this.customAttributeSingleValue || '').toLowerCase();
        return this.checkboxOptions.find(opt => opt.id === v) || null;
      },
      set(option) {
        this.customAttributeSingleValue = option ? option.id : '';
      },
    },
  },
  methods: {
    removeAction() {
      this.$emit('removeAction');
    },
    resetAction() {
      this.$emit('resetAction');
    },
    onActionNameChange(value) {
      this.actionNameAsSelectModel = value;
      this.resetAction();
    },
  },
};
</script>

<template>
  <li class="list-none py-2 first:pt-0 last:pb-0">
    <div
      class="flex flex-col gap-2"
      :class="{ 'animate-wiggle': errorMessage }"
    >
      <div class="flex items-center gap-2">
        <SingleSelect
          :model-value="actionNameAsSelectModel"
          :options="actionTypesAsOptions"
          :dropdown-max-height="dropdownMaxHeight"
          disable-deselect
          class="flex-shrink-0"
          @update:model-value="onActionNameChange"
        />
        <template v-if="showActionInput && !isVerticalLayout">
          <SingleSelect
            v-if="inputType === 'search_select'"
            v-model="action_params"
            :options="dropdownValues"
            :dropdown-max-height="dropdownMaxHeight"
          />
          <MultiSelect
            v-else-if="inputType === 'multi_select'"
            v-model="action_params"
            :options="dropdownValues"
            :dropdown-max-height="dropdownMaxHeight"
          />
          <CheckBoxSelect
            v-else-if="inputType === 'checkbox_select'"
            v-model="action_params"
            :options="dropdownValues"
          />

          <div
            v-else-if="inputType === 'custom_attribute_array'"
            class="flex flex-col gap-1 w-full"
          >
            <SingleSelect
              v-model="customAttributeSelected"
              :options="customAttributeOptions"
              :placeholder="$t('FORMS.MULTISELECT.SELECT')"
              :dropdown-max-height="dropdownMaxHeight"
            />

            <div
              v-if="
                customAttributeSelectedDisplayType === 'list' &&
                customAttributeValuesOptions.length
              "
              class="w-full"
            >
              <MultiSelect
                v-model="customAttributeSelectedValues"
                :options="customAttributeValuesOptions"
                :placeholder="$t('FORMS.MULTISELECT.SELECT')"
                :dropdown-max-height="dropdownMaxHeight"
              />
            </div>

            <div
              v-else-if="
                customAttributeSelected &&
                customAttributeSelectedDisplayType === 'checkbox'
              "
              class="w-full"
            >
              <SingleSelect
                v-model="customAttributeCheckboxOption"
                :options="checkboxOptions"
                :placeholder="$t('FORMS.MULTISELECT.SELECT')"
                :dropdown-max-height="dropdownMaxHeight"
              />
            </div>

            <div
              v-else-if="
                customAttributeSelected &&
                customAttributeSelectedDisplayType === 'date'
              "
              class="w-full"
            >
              <NextInput
                v-model="customAttributeSingleValue"
                type="date"
                size="sm"
              />
            </div>

            <div
              v-else-if="
                customAttributeSelected &&
                customAttributeSelectedDisplayType === 'link'
              "
              class="w-full"
            >
              <NextInput
                v-model="customAttributeSingleValue"
                type="url"
                size="sm"
                :placeholder="
                  $t('AUTOMATION.ACTION.CUSTOM_ATTRIBUTE_VALUE_PLACEHOLDER')
                "
              />
            </div>

            <div v-else-if="customAttributeSelected" class="w-full">
              <NextInput
                v-model="customAttributeSingleValue"
                type="text"
                size="sm"
                :placeholder="
                  $t('AUTOMATION.ACTION.CUSTOM_ATTRIBUTE_VALUE_PLACEHOLDER')
                "
              />
            </div>
          </div>

          <NextInput
            v-else-if="inputType === 'slack_channel'"
            v-model="action_params"
            type="text"
            size="sm"
            :placeholder="
              $t('AUTOMATION.ACTION.SLACK_CHANNEL_INPUT_PLACEHOLDER')
            "
          />
          <NextInput
            v-else-if="inputType === 'email'"
            v-model="action_params"
            type="email"
            size="sm"
            :placeholder="$t('AUTOMATION.ACTION.EMAIL_INPUT_PLACEHOLDER')"
          />
          <NextInput
            v-else-if="inputType === 'url'"
            v-model="action_params"
            type="url"
            size="sm"
            :placeholder="$t('AUTOMATION.ACTION.URL_INPUT_PLACEHOLDER')"
          />
          <AutomationActionFileInput
            v-else-if="inputType === 'attachment'"
            v-model="action_params"
            :initial-file-name="initialFileName"
          />
        </template>
        <NextButton
          v-if="!isMacro"
          sm
          solid
          slate
          icon="i-lucide-trash"
          class="flex-shrink-0"
          @click="removeAction"
        />
      </div>
      <AutomationActionTeamMessageInput
        v-if="inputType === 'team_message'"
        v-model="action_params"
        :teams="dropdownValues"
        :dropdown-max-height="dropdownMaxHeight"
      />
      <WootMessageEditor
        v-if="inputType === 'textarea'"
        v-model="castMessageVmodel"
        rows="4"
        enable-variables
        :placeholder="$t('AUTOMATION.ACTION.TEAM_MESSAGE_INPUT_PLACEHOLDER')"
        class="[&_.ProseMirror-menubar]:hidden px-3 py-1 bg-n-alpha-1 rounded-lg outline outline-1 outline-n-weak dark:outline-n-strong"
      />
      <SlackNotificationInput
        v-if="inputType === 'slack_notify'"
        v-model="action_params"
      />
    </div>
    <span v-if="errorMessage" class="text-sm text-n-ruby-11">
      {{ errorMessage }}
    </span>
  </li>
</template>
